import React from "react";
import { Form, Button } from "react-bootstrap";
import {
  FiUser,
  FiMail,
  FiLock,
  FiEye,
  FiEyeOff,
  FiInfo,
  FiUserPlus,
  FiGitBranch,
  FiShield,
} from "react-icons/fi";
import { validateEmail } from "../../../../utilities/helpers";
import { USER_ROLES, USER_ROLES_MAP } from "../../../../utilities/constant";

function IconField({ icon: Icon, children, hasTrailing, trailingIcon: TrailingIcon }) {
  return (
    <div
      className={`ccf-input-with-icon${hasTrailing ? " ccf-input-with-icon--trailing" : ""}${
        TrailingIcon ? " ccf-input-with-icon--icon-right" : ""
      }`}
    >
      {!TrailingIcon && Icon && (
        <span className="ccf-input-with-icon__icon">
          <Icon size={18} />
        </span>
      )}
      {children}
      {TrailingIcon && (
        <span className="ccf-input-with-icon__icon ccf-input-with-icon__icon--right">
          <TrailingIcon size={18} />
        </span>
      )}
    </div>
  );
}

export default function DefaultCallCentreUserForm({
  name,
  setName,
  username,
  setUsername,
  email,
  onEmailChange,
  password,
  setPassword,
  confirmPassword,
  setConfirmPassword,
  showPassword,
  setShowPassword,
  signupEmailOtp,
  setSignupEmailOtp,
  emailVerified,
  signupEmailBusy,
  onSendCode,
  onVerifyOtp,
  roleValue,
  setRoleValue,
  roleOptions,
  hideRoleSelect,
  flowType,
  setFlowType,
  aclValue,
  setAclValue,
  aclList,
}) {
  const isCallCentreAdmin = roleValue === USER_ROLES.CALL_CENTRE_ADMIN;
  const title = isCallCentreAdmin
    ? "Default Call Centre Admin"
    : roleValue && USER_ROLES_MAP[roleValue]
      ? `Default ${USER_ROLES_MAP[roleValue]}`
      : "Default Call Centre User";
  const emailError = email.trim() ? validateEmail(email) : null;

  return (
    <section className="default-user-panel">
      <div className="default-user-panel__header">
        <span className="default-user-panel__header-icon">
          <FiUser size={22} />
        </span>
        <div>
          <h5 className="default-user-panel__title">{title}</h5>
          <p className="default-user-panel__subtitle">
            Primary login for this call centre (same fields as call centre user create).
          </p>
        </div>
      </div>
      <hr className="default-user-panel__divider" />

      <div className="row g-3 default-user-panel__fields">
        <Form.Group className="col-12 col-lg-4 form-group mb-0">
          <Form.Label>
            Full Name <span className="text-danger">*</span>
          </Form.Label>
          <IconField icon={FiUser}>
            <Form.Control
              className="ccf-input-with-icon__control"
              placeholder="Full name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={100}
            />
          </IconField>
        </Form.Group>

        <Form.Group className="col-12 col-lg-4 form-group mb-0">
          <Form.Label>
            Username <span className="text-danger">*</span>
          </Form.Label>
          <IconField icon={FiUser}>
            <Form.Control
              className="ccf-input-with-icon__control"
              placeholder="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              maxLength={50}
            />
          </IconField>
        </Form.Group>

        {!hideRoleSelect ? (
          <Form.Group className="col-12 col-lg-4 form-group mb-0 default-user-panel__role-col">
            <Form.Label>
              Role <span className="text-danger">*</span>
            </Form.Label>
            <IconField icon={FiUserPlus}>
              <Form.Select
                required
                className="ccf-input-with-icon__control ccf-input-with-icon__select ccf-input-with-icon__select--acl"
                value={roleValue}
                onChange={(e) => setRoleValue(e.target.value)}
              >
                {roleOptions.length === 0 ? (
                  <option value="">No roles available</option>
                ) : (
                  roleOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))
                )}
              </Form.Select>
            </IconField>
          </Form.Group>
        ) : null}

        {isCallCentreAdmin ? (
          <Form.Group className="col-12 col-lg-4 form-group mb-0">
            <Form.Label>
              Flow Type <span className="text-danger">*</span>
            </Form.Label>
            <IconField icon={FiGitBranch}>
              <Form.Select
                required
                className="ccf-input-with-icon__control ccf-input-with-icon__select"
                value={flowType}
                onChange={(e) => setFlowType(e.target.value)}
              >
                <option value="">Select flow type</option>
                <option value="0">Asterisk</option>
                <option value="1">Jambonz</option>
              </Form.Select>
            </IconField>
          </Form.Group>
        ) : null}

        <Form.Group className="col-12 col-lg-4 form-group mb-0">
          <Form.Label>
            Password <span className="text-danger">*</span>
          </Form.Label>
          <IconField icon={FiLock} hasTrailing>
            <Form.Control
              type={showPassword ? "text" : "password"}
              className="ccf-input-with-icon__control ccf-input-with-icon__control--trailing"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button
              type="button"
              className="ccf-input-with-icon__trailing-btn"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
              <span className="ms-1">Show</span>
            </button>
          </IconField>
        </Form.Group>

        <Form.Group className="col-12 col-lg-4 form-group mb-0">
          <Form.Label>
            Confirm Password <span className="text-danger">*</span>
          </Form.Label>
          <IconField icon={FiLock}>
            <Form.Control
              type="password"
              className="ccf-input-with-icon__control"
              placeholder="Confirm password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </IconField>
        </Form.Group>

        <Form.Group className="col-12 col-lg-4 form-group mb-0 default-user-panel__acl-col">
          <Form.Label>
            ACL Settings <span className="text-danger">*</span>
          </Form.Label>
          <IconField icon={FiShield}>
            <Form.Select
              className="ccf-input-with-icon__control ccf-input-with-icon__select ccf-input-with-icon__select--acl"
              value={aclValue ?? ""}
              onChange={(e) => setAclValue(e.target.value)}
            >
              <option value="">Select ACL</option>
              {(aclList || []).map((acl) => (
                <option key={acl.id} value={acl.id}>
                  {acl.acl_name}
                  {acl.default_for_role === roleValue ? " (Default)" : ""}
                </option>
              ))}
            </Form.Select>
          </IconField>
          {!(aclList || []).length ? (
            <small className="text-muted d-block mt-1">
              No ACL profiles for this role. Create one under ACL Settings first.
            </small>
          ) : null}
        </Form.Group>

        <Form.Group className="col-12 form-group mb-0 default-user-panel__email-col">
          <Form.Label>
            Email <span className="text-danger">*</span>
          </Form.Label>
          <IconField icon={FiMail}>
            <Form.Control
              type="email"
              className={`ccf-input-with-icon__control${emailVerified ? " is-verified-email" : ""}`}
              placeholder="Email"
              value={email}
              onChange={onEmailChange}
              maxLength={100}
            />
          </IconField>
          <div className="default-user-email-verify">
            <Form.Control
              type="text"
              inputMode="numeric"
              className="default-user-email-verify__otp"
              placeholder="6-digit code"
              maxLength={6}
              value={signupEmailOtp}
              onChange={(e) =>
                setSignupEmailOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
              }
              disabled={signupEmailBusy}
            />
            <Button
              type="button"
              variant="outline-primary"
              size="sm"
              className="default-user-email-verify__btn default-user-email-verify__btn--send"
              disabled={signupEmailBusy || !email.trim() || !!emailError}
              onClick={onSendCode}
            >
              Send code
            </Button>
            <Button
              type="button"
              variant="outline-success"
              size="sm"
              className="default-user-email-verify__btn default-user-email-verify__btn--verify"
              disabled={signupEmailBusy || signupEmailOtp.length !== 6}
              onClick={onVerifyOtp}
            >
              Verify OTP
            </Button>
            {emailVerified && (
              <span className="text-success small default-user-email-verify__ok">
                Email verified
              </span>
            )}
          </div>
        </Form.Group>
      </div>

      <div className="default-user-panel__info-banner">
        <span className="default-user-panel__info-icon-wrap" aria-hidden>
          <FiInfo size={16} />
        </span>
        <span>
          Select the ACL for this admin. For Agent or Team Lead, allocate at least one matching
          licence in the section below.
        </span>
      </div>
    </section>
  );
}
